import { map, Observable, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders, HttpEvent } from '@angular/common/http';

/** Models */
import { FileModel } from '../models/file-model';

/** Environment */
import { environment } from '../../environments/environment';
import { ToasterService } from '../utils/toaster.service';
import { ApiErrorMessages, SUPPRESS_ERROR_TOAST_HEADER } from '../utils/api-error.utils';
import {
  AccountsPayable,
  AccountsPayableReadModel,
  AccountsPayableWriteModel,
  CoaCategory,
  Store,
  VendorSubsidiary,
} from '../models/accounts-payable';
import { StoreGroup } from '../models/response/store-groups';
import { Ocr } from '../models/ocr';
import { ProcessReceiptResponse } from '../models/process-receipt';
import { StoreSearchModel } from '../models/store-search-model';
import { AccountPayloadRequest } from '../models/request/account-payable-request-model';
import { StorageService } from './storage.service';
import { StorageKey } from '../models/common';
import { SearchRequest, SearchType } from '../models/search';
import { SearchService } from './search.service';

@Injectable({
  providedIn: 'root',
})
export class AccountsPayableService {
  private ctrlUrl: string = `${environment.apiUrl}/v1/AccountsPayable`;
  private ctrlUrlDraft: string = `${environment.apiUrl}/v1/AccountsPayable/draft`;
  private ocrURL: string = `${environment.ocrUrl}`;

  byPassedHttp: HttpClient;
  constructor(
    private http: HttpClient,
    private toasterSvc: ToasterService,
    private httpBackend: HttpBackend,
    private storageService: StorageService,
    private searchSvc: SearchService
  ) {
    this.byPassedHttp = new HttpClient(httpBackend);
  }

  public uploadAttachmentAsync(formData: FormData) {
    return this.http.post<FileModel>(
      `${this.ctrlUrl}/upload/attachment`,
      formData
    );
  }

  /** Same upload endpoint with HttpEvent progress for attachment UI. */
  uploadAttachmentWithProgress(formData: FormData) {
    return this.http.post<FileModel>(
      `${this.ctrlUrl}/upload/attachment`,
      formData,
      { reportProgress: true, observe: 'events' }
    );
  }

  withdrawRequest(id: string) {
    return this.http
      .patch<void>(
        `${environment.apiUrl}/v1/servicerequest/withdraw/${id}`,
        null
      )
      .pipe(
        tap(() => {
          this.toasterSvc.showSuccess('Request withdrawn successfully');
        })
      );
  }

  getDetails(id: string) {
    return this.http.get<AccountsPayableReadModel>(`${this.ctrlUrl}/${id}`);
  }

  getPeriods(): Observable<{ id: number; name: string }[]> {
    return this.http.get<{ id: number; name: string }[]>(`${this.ctrlUrl}/period`);
  }

  getVendorSubsidiary(vendorId: string) {
    return this.http.get<VendorSubsidiary[]>(`/v1/vendor/search/subsidiary/${vendorId}`);
  }

  getSubsidiaries() {
    return this.http.get<VendorSubsidiary[]>(`/v1/vendor/search/subsidiary`);
  }

  getStoreGroups(companyId: string) {
    return this.http
      .get<StoreGroup[]>(`/v1/store/storegroup/company/${companyId}`)
      .pipe(
      // catchError((error) => {
      //   this.toasterSvc.showError(
      //     error?.message || 'Something went wrong. Please try again later.'
      //   );
      //   return EMPTY;
      // })
    );
  }

  getStores(payload: StoreSearchModel) {
    return this.http.post<Store[]>(`/v1/accountspayable/search/stores/`, payload);
  }

  getStoresByCompany(companyId: string) {
    const payload: StoreSearchModel = { companyId };
    return this.http.post<Store[]>(`/v1/accountspayable/search/stores/`, payload);
  }

  public getStoresForGroups(storeGroupId: any) {
    return this.http.get<Store>(`${this.ctrlUrl}/storegroup/store/${storeGroupId}`);
  }

  getCoaCateogry(subsidiaryId: string, suppressErrorToast = false) {
    let headers = new HttpHeaders();
    if (suppressErrorToast) {
      headers = headers.set(SUPPRESS_ERROR_TOAST_HEADER, 'true');
    }
    return this.http.get<CoaCategory[]>(
      `${this.ctrlUrl}/search/chartofaccounts/${subsidiaryId}`,
      { headers }
    );
  }

  getSecurityDepositCoaCategory(subsidiaryId: string) {
    return this.http.get<unknown>(
      `${this.ctrlUrl}/search/securitydeposit/chartofaccounts/${subsidiaryId}`
    );
  }

  getCoaCategories() {
    return this.http.get<CoaCategory[]>(`${this.ctrlUrl}/search/chartofaccounts`);
  }

  getCoaCategoriesBank() {
    return this.http.get<CoaCategory[]>(`${this.ctrlUrl}/search/chartofaccounts/bank`);
  }

  getOutstandingVendorBills(vendorId: string) {
    const payload: SearchRequest = {
      type: SearchType.accountsPayable,
      page: 1,
      pageSize: 500,
      AccountPayableFilters: {
        vendorId,
        status: ['Approved', 'PaymentPending'],
      },
    };
    return this.searchSvc.searchAsync<AccountsPayable>(payload, 'servicerequest').pipe(
      map((res) => (res?.items ?? []).filter((b) => (b.accountsPayableDetails?.remainingAmount ?? 0) > 0))
    );
  }

  createCollatedPayment(payload: {
    vendorId: string;
    subsidiaryOrStoreGroupId: string;
    accountId: string;
    date: Date;
    memo?: string;
    interCompanyPayment: boolean;
    totalPaymentAmount: number;
    billAllocations?: { serviceRequestId: string; billId?: string; amount: number }[];
  }) {
    return this.http.post<AccountsPayableReadModel>(`${this.ctrlUrl}/collated-payment`, payload);
  }

  createRequest(
    payload: AccountsPayableWriteModel,
    isDraft: boolean = false
  ): Observable<AccountsPayableReadModel> {
    return this.http
      .post<AccountsPayableReadModel>(isDraft ? `${this.ctrlUrlDraft}` : `${this.ctrlUrl}`, payload)
      .pipe(
        tap(() => {
          this.toasterSvc.showSuccess(
            isDraft ? 'Your request has been saved as a draft.' : 'Request created successfully'
          );
        })
      );
  }

  updateRequest(
    payload: AccountsPayableWriteModel,
    isDraft: boolean = false,
    options?: { showAsCreated?: boolean }
  ): Observable<AccountsPayableReadModel> {
    return this.http
      .put<AccountsPayableReadModel>(isDraft ? `${this.ctrlUrlDraft}` : `${this.ctrlUrl}`, payload)
      .pipe(
        tap(() => {
          const message = isDraft
            ? 'Your request has been saved as a draft.'
            : options?.showAsCreated
              ? 'Request created successfully'
              : 'Request updated successfully';
          this.toasterSvc.showSuccess(message);
        })
      );
  }

  delteRequestItem(id: string): Observable<void> {
    return this.http.delete<void>(`${this.ctrlUrl}/${id}`).pipe(
      tap(() => {
        this.toasterSvc.showSuccess('Item deleted successfully');
      })
    );
  }

  // approveRequest(id: string): Observable<void> {
  //   return this.http.patch<void>(`${this.ctrlUrl}/approve/${id}`, null).pipe(
  //     tap(() => {
  //       this.toasterSvc.showSuccess('Request approved successfully');
  //     }),
  //     catchError((error) => {
  //       this.toasterSvc.showError(
  //         error?.message || 'Something went wrong. Please try again later.'
  //       );
  //       return EMPTY;
  //     })
  //   );
  // }

  approveRequest(AccountPayloadRequest: AccountPayloadRequest): Observable<void> {
    return this.http.post<void>(`${this.ctrlUrl}/approve`, AccountPayloadRequest);
  }


  rejectRequest(AccountPayloadRequest: AccountPayloadRequest): Observable<void> {
    return this.http.post<void>(`${this.ctrlUrl}/reject`, AccountPayloadRequest);
  }

  returnRequest(AccountPayloadRequest: AccountPayloadRequest): Observable<void> {
    return this.http.post<void>(`${this.ctrlUrl}/return`, AccountPayloadRequest);
  }


  referRequest(id: string): Observable<void> {
    return this.http
      .patch<void>(`${this.ctrlUrl}/refer/admin/${id}`, null)
      .pipe(
        tap(() => {
          this.toasterSvc.showSuccess('Request referred successfully');
        })
      );
  }

  integrateRequest(id: string): Observable<void> {
    return this.http
      .post<void>(`${this.ctrlUrl}/netsuitebill`, {
        id,
      })
      .pipe(
        tap(() => {
          this.toasterSvc.showSuccess('Request integrated successfully');
        })
      );
  }

  /**
   * "Auto-fill data" on the AP creation form — POST v1/AccountsPayable/ocr.
   *
   * Used to call `{ocrUrl}/ProcessFile` directly on DocumentAIService via `byPassedHttp`, which
   * deliberately skips `CustomHttpInterceptor` — so this call never carried the `Role`/`Company`
   * headers every other AP call does. Routing it through the Backend's own composite endpoint
   * (Claude Vision fields + DocumentAIService's layout boxes, merged server-side — see Plan 007
   * Part A) fixes that for free: `this.http` attaches the same `Authorization`/`Role`/`RoleName`/
   * `Company`/`ApiKey` headers as everything else, and already handles a `FormData` body
   * correctly. The response shape is unchanged (entities + pageLayouts), so nothing downstream —
   * `ap-ocr.util.ts`, the overlay/click-to-insert code in `ap-document-panel` — needed to change.
   */
  processDocument(file: File) {
    const formData = new FormData();
    formData.append('document', file);

    return this.http.post<Ocr>(`${this.ctrlUrl}/ocr`, formData);
  }

  /** Expense Scan Receipt — POST /ProcessReceipt (single document). */
  processReceipt(file: File) {
    const formData = new FormData();
    formData.append('document', file);

    const token = this.storageService.get(StorageKey.jwt);
    let headers = new HttpHeaders({
      Accept: '*/*',
    });
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.byPassedHttp.post<ProcessReceiptResponse>(
      `${this.ocrURL}/ProcessReceipt`,
      formData,
      { headers }
    );
  }

  processFile(
    file: File,
    options?: { reportProgress?: boolean; observe?: 'body' | 'events' | 'response' },
    documentType: 'AccountsPayable' | 'expense' = 'AccountsPayable'
  ) {
    const formData = new FormData();
    formData.append('documents', file);
    formData.append('type', documentType);

    // Get JWT token for authentication
    const token = this.storageService.get(StorageKey.jwt);

    let headers = new HttpHeaders({
      Accept: '*/*',
    });

    if (token) {
      // byPassedHttp skips CustomHttpInterceptor — send Role/Company like other API calls.
      headers = headers
        .set('Authorization', `Bearer ${token}`)
        .set('Role', localStorage.getItem('selectedRoleId') || '')
        .set('Company', localStorage.getItem('selectedCompanyId') || '');
    }

    const requestOptions: any = {
      headers,
    };

    if (options) {
      if (options.reportProgress !== undefined) {
        requestOptions.reportProgress = options.reportProgress;
      }
      if (options.observe !== undefined) {
        requestOptions.observe = options.observe;
      }
    }

    return this.byPassedHttp.post<FileModel>(
        `${this.ocrURL}/process-batch`,
        formData,
        requestOptions
      );
  }

  /** Sends all files as a single multipart request (BE binds `documents` as a collection). */
  processFilesBatch(
    files: File[],
    documentType: 'AccountsPayable' | 'expense' = 'AccountsPayable'
  ) {
    const formData = new FormData();
    files.forEach((file) => formData.append('documents', file));
    formData.append('type', documentType);

    const token = this.storageService.get(StorageKey.jwt);
    let headers = new HttpHeaders({
      Accept: '*/*',
    });

    if (token) {
      headers = headers
        .set('Authorization', `Bearer ${token}`)
        .set('Role', localStorage.getItem('selectedRoleId') || '')
        .set('Company', localStorage.getItem('selectedCompanyId') || '');
    }

    return this.byPassedHttp.post<FileModel[]>(
      `${this.ocrURL}/process-batch`,
      formData,
      { headers }
    );
  }

  getFileStream(payload: any) {
    return this.http.post<any>(`${this.ctrlUrl}/stream`, payload);
  }

  viewFile(file: FileModel, base64Stream: string, toasterService: ToasterService): void {
    const supportedPreviewTypes = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/bmp',
      'image/webp',
      'image/svg+xml',
      'image/avif',
      'image/tiff'
    ];

    if (!supportedPreviewTypes.includes(file.contentType)) {
      toasterService.showError(ApiErrorMessages.previewNotSupported);
      return;
    }

    // Convert Base64 to Blob
    const fileBlob = this.base64ToBlob(base64Stream, file.contentType);
    const blobUrl = URL.createObjectURL(fileBlob);

    // Open in a new tab for preview
    window.open(blobUrl, '_blank');
  }

  private base64ToBlob(base64: string, contentType: string = ''): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }
}
